import { useEffect } from "react";
import { Badge, Button, Checkbox, Group, Modal, Radio, Space, TextInput, Title } from "@mantine/core";
import { useForm } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import { IconPencil } from "@tabler/icons-react";

import { useProgramRegistrationState } from "../../state/programregistration.state";
import { ReservationLinkStatus, ReservationLinkType } from "../../models/programregistration.models";

interface FormFields {
	Id: number;
	Type: ReservationLinkType;
	Status: ReservationLinkStatus;
	ReservationNumber: string;
	StayAddress: string;
	Verified: boolean;
}

const ReservationLinkEditor = () => {
	const { programRegistration, updateProgramRegistration, isDeleted, isCancelled, status } = useProgramRegistrationState();
	const [opened, { open, close }] = useDisclosure(false);

	const form = useForm<FormFields>({
		validate: {
			ReservationNumber: (value, values) => {
				if (values.Type === ReservationLinkType.ReservationNumber && value.trim().length === 0) {
					return 'Reservation Number is required';
				}
				return null;
			},
			StayAddress: (value, values) => {
				if (values.Type === ReservationLinkType.PrivateRental && value.trim().length === 0) {
					return 'Stay Address is required';
				}
				return null;
			}
		}
	});

	useEffect(() => {
		if (programRegistration) {
			form.setValues({
				Id: programRegistration.Data.ReservationLink?.Id ?? 0,
				Type: programRegistration.Data.ReservationLink?.Type ?? ReservationLinkType.None,
				Status: programRegistration.Data.ReservationLink?.Status ?? ReservationLinkStatus.ManuallyLinked,
				ReservationNumber: programRegistration.Data.ReservationLink?.Type === ReservationLinkType.ReservationNumber ? programRegistration.Data.ReservationLink?.ReservationNumber ?? "" : "",
				StayAddress: (programRegistration.Data.ReservationLink?.Type === ReservationLinkType.PrivateRental || programRegistration.Data.ReservationLink?.Type === ReservationLinkType.None) ? programRegistration.Data.ReservationLink?.StayAddress ?? "" : "",
				Verified: programRegistration.Data.ReservationLink?.Verified ?? false
			});
			form.resetDirty();
			form.resetTouched();
		}
	}, [opened, programRegistration]);

	useEffect(() => {
		if (form.isDirty()) {
			form.setFieldValue('Status', ReservationLinkStatus.ManuallyLinked);
		}
	}, [form.values]);

	const handleEdit = () => {
		open();
	}

	const handleSubmit = (values: FormFields) => {
		if (programRegistration) {
			const updatedRegistration = {
				...programRegistration,
				Data: {
					...programRegistration.Data,
					ReservationLink: {
						Id: values.Id,
						Type: values.Type,
						Status: values.Status,
						ReservationNumber: values.Type === ReservationLinkType.ReservationNumber ? values.ReservationNumber : undefined,
						StayAddress: (values.Type === ReservationLinkType.PrivateRental || values.Type === ReservationLinkType.None) ? values.StayAddress : undefined,
						Verified: values.Verified
					}
				}
			};
			updateProgramRegistration(updatedRegistration);
		}
		close();
	}

	return <>
		<Button variant="light" size="xs" leftSection={<IconPencil />} onClick={handleEdit} disabled={!status.canEdit || isCancelled || isDeleted}>Edit</Button>

		<Modal opened={opened} onClose={close} title={<Title order={3} component="span">Reservation Number / Stay Location</Title>}>
			<form onSubmit={form.onSubmit((values) => handleSubmit(values))}>
				<Radio.Group name="Type" {...form.getInputProps('Type')}>
					<Radio value="reservation-number" label={`${import.meta.env.VITE_ORG} Reservation Number`} mb="sm" />
					<Radio value="none" label={`${import.meta.env.VITE_ORG} Stay Location`} mb="sm" />
					<Radio value="private-rental" label="Private Rental / Stay Location" mb="sm" />
				</Radio.Group>

				{form.values.Type === ReservationLinkType.ReservationNumber &&
					<TextInput label="Reservation Number" {...form.getInputProps('ReservationNumber')}></TextInput>
				}
				
				{form.values.Type === ReservationLinkType.PrivateRental &&
					<TextInput label="Stay Address" {...form.getInputProps('StayAddress')}></TextInput>
				}
				
				{form.values.Type === ReservationLinkType.None &&
					<TextInput label="Room or Site #" {...form.getInputProps('StayAddress')}></TextInput>
				}

				{form.values.Status === ReservationLinkStatus.AutomaticallyLinked ? (
					<Badge size="sm" color="green" variant="dot">Automatically matched</Badge>
				) : form.values.Status === ReservationLinkStatus.ManuallyLinked ? (
					<Badge size="sm" color="orange" variant="dot">Manually entered</Badge>
				) : null}

				<Space h="md" />

				<Checkbox label={`Information has been verified`} {...form.getInputProps('Verified', { type: 'checkbox' })} />

				<Space h="md" />

				<Group justify="end">
					<Button type="button" mt="md" variant="subtle" onClick={close}>Cancel</Button>
					<Button type="submit" mt="md" variant="filled" disabled={!form.isDirty() && !form.isTouched()}>Update</Button>
				</Group>
			</form>
		</Modal>
	</>
}

export default ReservationLinkEditor;